import React, { useEffect } from 'react';
import ProtobufObjectTable from '../ProtobufObjectTable';
import useProtobufCollection from './useProtobufCollection';
import { useProtobufDeviceContext } from './ProtobufDeviceContext';
import './ProtobufCollectionTable.css';

const ProtobufCollectionTable = ({
  statusUuid,
  recordUuid,
  protobufType,
  protobufRoot,
  protobufProtocolRoot,
  editable = true
}) => {
  const { protobufProtocolService, connectionState } = useProtobufDeviceContext();
  const isConnected = connectionState && connectionState.isConnected;
  const {
    // State
    collectionStatus,
    records,
    loading,
    error,
    collectionLoaded,
    loadingProgress,
    initialized,
    // Actions
    clearCollection,
    loadCollection,
    addItem,
    deleteLastItem,
    updateRecord,
    finishTransaction,
    clearError,
    loadCollectionStatus
  } = useProtobufCollection(statusUuid, recordUuid, protobufType, protobufRoot, protobufProtocolRoot, protobufProtocolService);

  // Load collection status when isConnected changes
  useEffect(() => {
    console.log('isConnected effect', isConnected);
    if (isConnected && loadCollectionStatus) {
      console.log('Loading collection status');
      loadCollectionStatus();
    }
    // Only run on mount or when isConnected changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // Also load collection status after service is initialized
  useEffect(() => {
    if (initialized && loadCollectionStatus) {
      console.log('Service initialized, loading collection status');
      loadCollectionStatus();
    }
  }, [initialized, loadCollectionStatus]);

  return (
    <div className="protobuf-collection-table">
      {/* Connection Status Indicator */}
      <div className="connection-status" style={{ 
        marginBottom: '15px', 
        padding: '8px 12px', 
        borderRadius: '4px', 
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}>
        <div style={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: isConnected ? '#4CAF50' : '#f44336'
        }}></div>
        <span style={{ color: isConnected ? '#4CAF50' : '#f44336', fontWeight: '500' }}>
          {isConnected ? 'Connected' : 'Disconnected'}
        </span>
      </div>

      {/* Error Display - Non-blocking */}
      {error && (
        <div className="protobuf-collection-error">
          <h4>⚠️ Connection Error</h4>
          <p>{error}</p>
          <div style={{ fontSize: '12px', color: '#666', marginBottom: '10px', fontStyle: 'italic' }}>
            You can still view the interface, but BLE operations may not work until the connection is restored.
          </div>
          <div style={{ fontSize: '12px', color: '#999', marginBottom: '10px' }}>
            {new Date().toLocaleTimeString()}
          </div>
          <button onClick={clearError} className="error-dismiss-button">
            Dismiss
          </button>
        </div>
      )}

      {/* Loading Progress */}
      {loadingProgress.loading && (
        <div className="loading-progress">
          <div className="progress-header">
            <h4>Loading Collection</h4>
            <span className="progress-text">
              {loadingProgress.current} / {loadingProgress.total} records loaded
            </span>
          </div>
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill"
              style={{ 
                width: `${loadingProgress.total > 0 ? (loadingProgress.current / loadingProgress.total) * 100 : 0}%` 
              }}
            />
          </div>
        </div>
      )}

      {/* Collection Status */}
      <div className="collection-status">
        <h4>Collection Status</h4>
        {collectionStatus && (
          <ProtobufObjectTable
            object={collectionStatus}
            typeName="ProtobufCollectionStatus"
            protobufRoot={protobufProtocolRoot}
            editable={false}
            readOnlyFields={['maxRecords', 'currentRecords', 'currentRecord', 'operationSuccess']}
          />
        )}
        {!collectionStatus && !error && <div>Loading collection status...</div>}
      </div>

      {/* Control Buttons */}
      <div className="collection-controls">
        <button 
          onClick={loadCollection} 
          disabled={loading || !isConnected}
          className="control-button load-button"
          title={!isConnected ? "BLE connection required" : ""}
        >
          {loading ? 'Loading...' : 'Load Collection'}
        </button>

        <button 
          onClick={clearCollection} 
          disabled={loading || !isConnected}
          className="control-button clear-button"
          title={!isConnected ? "BLE connection required" : ""}
        >
          {loading ? 'Clearing...' : 'Clear Collection'}
        </button>

        <button 
          onClick={finishTransaction} 
          disabled={loading || !isConnected}
          className="control-button finish-button"
          style={{ backgroundColor: '#4CAF50', color: 'white' }}
          title={!isConnected ? "BLE connection required" : ""}
        >
          {loading ? 'Finishing...' : 'Finish Transaction'}
        </button>
      </div>

      {/* Records */}
      <div className="collection-records">
        <h4>Records ({records.length})</h4>
        {loading && <div className="loading-indicator">Loading...</div>}
        
        {records.map((record, index) => {
          return (
            <div key={index} className="record-item">
              <div className="record-header">
                <h5>Record {index}</h5>
              </div>
              <ProtobufObjectTable
                object={record}
                typeName={protobufType}
                protobufRoot={protobufRoot}
                editable={editable}
                onChange={(key, newValue) => {
                  const updatedRecord = { ...record, [key]: newValue };
                  updateRecord(index, updatedRecord);
                }}
                recursive={true}
              />
            </div>
          );
        })}
        
        {records.length === 0 && !loading && (
          <div className="no-records">No records in collection</div>
        )}
        
        {/* Add/Delete buttons - show when collection has been loaded */}
        {collectionLoaded && (
          <div className="record-controls">
            <button 
              onClick={addItem} 
              disabled={loading || !isConnected}
              className="control-button add-button"
              style={{ backgroundColor: '#FF9800', color: 'white' }}
              title={!isConnected ? "BLE connection required" : ""}
            >
              Add Item
            </button>
            
            {records.length > 0 && (
              <button 
                onClick={deleteLastItem} 
                disabled={loading || !isConnected}
                className="control-button delete-button"
                style={{ backgroundColor: '#f44336', color: 'white' }}
                title={!isConnected ? "BLE connection required" : ""}
              >
                Delete Last Item
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProtobufCollectionTable; 